import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import { Button } from "@/components/ui/button";
import { Sparkles, TrendingUp, TrendingDown, ArrowRight, Wallet, Timer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, Legend,
} from "recharts";

const SYM = { USD: "$", NGN: "₦" };
function fmCcy(n, ccy = "USD") {
  if (n == null) return "—";
  const sym = SYM[ccy] || "$";
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sym}${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sym}${(n / 1e3).toFixed(1)}K`;
  return `${sym}${Number(n).toFixed(2)}`;
}
function fm(n) { return fmCcy(n, "USD"); }
function pct(n) { return n == null ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`; }

export default function ManagementDashboard() {
  const [data, setData] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const { filterRails, activeRail } = useCurrencyRail();

  const load = async () => {
    const { data } = await api.get("/reports/management-kpis");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const generateNarrative = async () => {
    if (!data) return;
    setNarrativeLoading(true);
    try {
      const { data: r } = await api.post("/ai/report-narrative", {
        report: "management",
        start: data.start,
        end: data.end,
        data: data.kpis,
      });
      setNarrative(r.narrative);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI narrative failed");
    } finally {
      setNarrativeLoading(false);
    }
  };

  if (!data) return <AppShell><div className="text-sm text-[var(--muted-text)]">Loading executive dashboard…</div></AppShell>;
  const k = data.kpis;

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">C-suite</div>
          <h1 className="font-display text-4xl mt-2">Management dashboard</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">Period {data.start} → {data.end}</p>
        </div>
        <Button data-testid="ai-narrative-btn" onClick={generateNarrative} disabled={narrativeLoading}
          className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8] h-11 px-5">
          <Sparkles className="w-4 h-4 mr-2" /> {narrativeLoading ? "Thinking…" : "AI executive summary"}
        </Button>
      </div>

      {narrative && (
        <div data-testid="ai-narrative-body" className="card-flat p-6 mb-8 border-l-4 border-l-[#E07A5F] whitespace-pre-line leading-relaxed text-[15px]">
          {narrative}
        </div>
      )}

      {/* KPI grid — cash-basis totals (mixed across currencies). Per-rail cards below. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi title="Revenue" value={fm(k.revenue)} delta={k.revenue_growth_pct} testId="kpi-revenue" />
        <Kpi title="Net income" value={fm(k.net_income)} delta={k.net_income_growth_pct} testId="kpi-net-income" />
        <Kpi title="Gross margin" value={k.gross_margin_pct == null ? "—" : `${k.gross_margin_pct}%`} testId="kpi-gross-margin" />
        <Kpi title="Net margin" value={k.net_margin_pct == null ? "—" : `${k.net_margin_pct}%`} testId="kpi-net-margin" />
        <Kpi title="Cash balance" value={fm(k.cash_balance)} icon={<Wallet className="w-4 h-4" />} testId="kpi-cash" />
        <Kpi title="Runway" value={k.runway_months == null ? "—" : `${k.runway_months} mo`} icon={<Timer className="w-4 h-4" />}
             hint={`Burn ${fm(k.monthly_burn)}/mo`} testId="kpi-runway" />
        <Kpi title="AR outstanding" value={fm(k.ar_outstanding)} hint={`DSO ${k.dso_days}d`} testId="kpi-ar" />
        <Kpi title="AP outstanding" value={fm(k.ap_outstanding)} hint={`DPO ${k.dpo_days}d`} testId="kpi-ap" />
      </div>

      {/* Per-currency KPIs — honors sidebar rail filter, no cross-currency margin math */}
      {(() => {
        const rails = filterRails(data.by_currency || []);
        if (rails.length === 0 || (activeRail === "ALL" && rails.length <= 1)) return null;
        return (
          <>
            {/* FX-consolidated card (iter 60j) — shows only when sidebar toggle is ON
                AND 2+ rails have activity. Sums revenue/net-income/expenses across
                USD + NGN using live Frankfurter rates. */}
            <div className="mb-4">
              <ConsolidatedRailCard
                rails={data.by_currency || []}
                testId="exec-consolidated"
                subtitle="revenue + net income + expenses across every rail"
                fields={[
                  { key: "revenue", label: "Revenue" },
                  { key: "net_income", label: "Net income",
                    color: (data.by_currency || []).reduce((a, r) => a + (r.net_income || 0), 0) >= 0 ? "text-[#4C956C]" : "text-[#E63946]" },
                  { key: "expenses", label: "Expenses", color: "text-[#B4283A]" },
                ]}
              />
            </div>
            <div data-testid="exec-by-currency" className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              {rails.map((r) => (
                <div key={r.currency} data-testid={`exec-rail-${r.currency}`} className="card-flat p-5 border-l-4"
                     style={{ borderLeftColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{r.currency} rail</div>
                    <div className="text-[10px] text-[var(--muted-text)]">native · no FX mixing</div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MiniKpi label="Revenue" value={fmCcy(r.revenue, r.currency)} delta={r.revenue_growth_pct} />
                    <MiniKpi label="Net income" value={fmCcy(r.net_income, r.currency)} delta={r.net_income_growth_pct} />
                    <MiniKpi label="Net margin" value={`${r.net_margin_pct}%`} />
                    <MiniKpi label="Expenses" value={fmCcy(r.expenses, r.currency)} />
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      })()}

      {/* Trend chart */}
      <div className="card-flat p-6 mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--muted-text)] mb-4">Revenue vs Expenses · last 6 months</div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={data.trend}>
              <CartesianGrid strokeDasharray="2 2" stroke="var(--divider)" />
              <XAxis dataKey="month" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fm(v)} />
              <Legend />
              <Bar dataKey="revenue" fill="#2F6B4A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" fill="#B4283A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="net" fill="#E07A5F" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {k.runway_months != null && k.runway_months < 6 && (
          <Alert testId="alert-runway" tone="warn" icon={<AlertTriangle className="w-4 h-4" />}
                 title="Runway is short"
                 body={`At current burn (${fm(k.monthly_burn)}/month) you have ${k.runway_months} months of cash. Consider reducing spend or raising capital.`} />
        )}
        {k.dso_days > 45 && (
          <Alert testId="alert-dso" tone="warn" icon={<Timer className="w-4 h-4" />}
                 title="Slow collections"
                 body={`DSO is ${k.dso_days} days. Chase overdue invoices — every 10 days saved frees ${fm(k.ar_outstanding * 10 / (k.dso_days || 1))} of cash.`} />
        )}
        {k.gross_margin_pct != null && k.gross_margin_pct < 30 && k.revenue > 0 && (
          <Alert testId="alert-margin" tone="warn" icon={<TrendingDown className="w-4 h-4" />}
                 title="Gross margin under pressure"
                 body={`Gross margin is ${k.gross_margin_pct}%. Review COGS or pricing — a 5-point lift adds ${fm(k.revenue * 0.05)}.`} />
        )}
        {k.revenue_growth_pct != null && k.revenue_growth_pct > 20 && (
          <Alert testId="alert-growth" tone="good" icon={<TrendingUp className="w-4 h-4" />}
                 title="Strong revenue growth"
                 body={`Revenue is ${pct(k.revenue_growth_pct)} vs prior period. Keep sales momentum and watch fulfillment capacity.`} />
        )}
      </div>
    </AppShell>
  );
}

function MiniKpi({ label, value, delta }) {
  const positive = typeof delta === "number" && delta > 0;
  const negative = typeof delta === "number" && delta < 0;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted-text)]">{label}</div>
      <div className="font-mono-data text-lg mt-1">{value}</div>
      {typeof delta === "number" && (
        <div className={`text-[10px] mt-0.5 inline-flex items-center gap-1 ${positive ? "text-[#2F6B4A]" : negative ? "text-[#B4283A]" : "text-[var(--muted-text)]"}`}>
          {pct(delta)}
        </div>
      )}
    </div>
  );
}

function Kpi({ title, value, delta, hint, icon, testId }) {
  const positive = typeof delta === "number" && delta > 0;
  const negative = typeof delta === "number" && delta < 0;
  return (
    <div data-testid={testId} className="card-flat p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted-text)]">{title}</div>
        {icon && <div className="text-[var(--muted-text)]">{icon}</div>}
      </div>
      <div className="font-mono-data text-2xl mt-2">{value}</div>
      {typeof delta === "number" && (
        <div className={`text-xs mt-2 inline-flex items-center gap-1 ${positive ? "text-[#2F6B4A]" : negative ? "text-[#B4283A]" : "text-[var(--muted-text)]"}`}>
          {positive ? <TrendingUp className="w-3.5 h-3.5" /> : negative ? <TrendingDown className="w-3.5 h-3.5" /> : null}
          {pct(delta)} vs prior period
        </div>
      )}
      {hint && !delta && <div className="text-xs text-[var(--muted-text)] mt-2">{hint}</div>}
    </div>
  );
}

function Alert({ tone = "warn", icon, title, body, testId }) {
  const tones = {
    warn: "border-l-[#D5A02A] bg-[#FBF7EC]",
    good: "border-l-[#2F6B4A] bg-[#E5F1E9]",
  };
  return (
    <div data-testid={testId} className={`card-flat p-4 border-l-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-sm font-medium">{icon}{title}</div>
      <div className="text-xs text-[var(--muted-text)] mt-2 leading-relaxed">{body}</div>
    </div>
  );
}
